import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import DataTable from "../components/DataTable";
import EntityForm from "../components/EntityForm";
import { StatusTextBadge as Badge } from "../../../components/base/StatusTextBadge";
import {
  fetchCrmObjects,
  fetchCrmObjectTypes,
  createCrmObject,
  updateCrmObject,
  deleteCrmObject,
  type CrmObject,
  type CrmObjectType,
  type CrmObjectTypeDef,
} from "../services/crmObjectsService";
import { pushToHubspot } from "../services/hubspotService";
import type { CrudColumn, CrudField, CrudRow } from "../types";

// ===========================================================================
// Live CRUD for the CRM entities the HubSpot suite integration brought in:
// tareas, notas, tickets, correos registrados, productos, partidas y
// cotizaciones (editables) más los espejos de Marketing Hub — formularios,
// envíos, campañas y segmentos — que HubSpot posee y aquí son de sólo lectura.
//
// Replaces the demo scaffolds those menu entries used to render. One component
// serves every type: the columns come from the caller, and the ADD/EDIT form is
// generated from the server-side catalog (fields, required, enums), so a new
// field on the backend shows up here without touching this file.
//
// Rows that already exist in HubSpot show a link badge; the row action pushes a
// record to HubSpot on demand instead of waiting for the next sync.
// ===========================================================================

interface CrmObjectsLiveProps {
  type: CrmObjectType;
  title: string;
  subtitle?: string;
  icon?: string;
  columns: CrudColumn<CrmObject & CrudRow>[];
  /** Fixed equality filters (e.g. `{ status: "pendiente" }` for a sub-list). */
  filters?: Record<string, string>;
}

/** Human labels for the enum values the catalog exposes, so selects read well. */
const ENUM_LABELS: Record<string, string> = {
  pendiente: "Pendiente",
  en_proceso: "En proceso",
  esperando: "Esperando",
  completada: "Completada",
  descartada: "Descartada",
  resuelto: "Resuelto",
  nuevo: "Nuevo",
  low: "Baja",
  medium: "Media",
  high: "Alta",
  todo: "Pendiente",
  call: "Llamada",
  email: "Correo",
  meeting: "Reunión",
  borrador: "Borrador",
  enviada: "Enviada",
  aprobada: "Aprobada",
  aceptada: "Aceptada",
  rechazada: "Rechazada",
  vencida: "Vencida",
};

const enumLabel = (v: string) => ENUM_LABELS[v] ?? v;

/** Guess a sensible input type for a field name the catalog didn't type for us. */
function fieldType(name: string): CrudField["type"] {
  if (/At$/.test(name)) return "date";
  if (/^(amount|price|cost|quantity|probability)$/.test(name)) return "number";
  if (/^(email|to|from)$/.test(name)) return "email";
  if (/^(body|notes|description|html)$/.test(name)) return "textarea";
  return "text";
}

/** Turn a camelCase field key into a Spanish-ish label for the form. */
const FIELD_LABELS: Record<string, string> = {
  title: "Título",
  subject: "Asunto",
  body: "Contenido",
  notes: "Notas",
  description: "Descripción",
  status: "Estado",
  priority: "Prioridad",
  type: "Tipo",
  dueAt: "Vence",
  completedAt: "Completada",
  noteAt: "Fecha",
  sentAt: "Enviado",
  expiresAt: "Vence",
  closedAt: "Cerrado",
  category: "Categoría",
  channel: "Canal",
  ownerId: "Responsable (id)",
  contactId: "Contacto (id)",
  companyId: "Empresa (id)",
  dealId: "Negocio (id)",
  quoteId: "Cotización (id)",
  leadId: "Lead (id)",
  productId: "Producto (id)",
  name: "Nombre",
  price: "Precio",
  cost: "Costo",
  sku: "SKU",
  amount: "Importe",
  quantity: "Cantidad",
  currency: "Moneda",
  direction: "Dirección",
  to: "Para",
  from: "De",
  html: "HTML",
  tags: "Etiquetas",
  publicUrlKey: "Clave pública",
};

const fieldLabel = (key: string) => FIELD_LABELS[key] ?? key;

export default function CrmObjectsLive({
  type,
  title,
  subtitle,
  icon,
  columns,
  filters,
}: CrmObjectsLiveProps) {
  const tc = buildThemeClasses();

  const [def, setDef] = useState<CrmObjectTypeDef | null>(null);
  const [rows, setRows] = useState<(CrmObject & CrudRow)[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState<null | { mode: "add" | "edit"; row?: CrmObject & CrudRow }>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [types, page] = await Promise.all([
        fetchCrmObjectTypes(),
        fetchCrmObjects<CrmObject & CrudRow>(type, { limit: 200, search: query || undefined, filters }),
      ]);
      setDef(types.find((t) => t.type === type) ?? null);
      setRows(page.rows);
      setTotal(page.total);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudo cargar la información");
    } finally {
      setLoading(false);
    }
    // `filters` is a literal from the caller; stringify to keep the dep stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, query, JSON.stringify(filters)]);

  useEffect(() => {
    load();
  }, [load]);

  /** Form fields generated from the catalog: enums become selects. */
  const formFields: CrudField[] = useMemo(() => {
    if (!def) return [];
    return def.fields.map((key) => {
      const options = def.enums[key];
      return {
        key,
        label: fieldLabel(key),
        type: options ? "select" : fieldType(key),
        required: def.required.includes(key),
        options: options?.map((v) => ({ value: v, label: enumLabel(v) })),
        defaultValue: def.defaults[key] as string | number | undefined,
      };
    });
  }, [def]);

  const handleSubmit = async (values: Record<string, string>) => {
    try {
      // Drop empty strings so a blank optional field doesn't overwrite a value.
      const body = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== ""));
      if (modal?.mode === "edit" && modal.row) {
        await updateCrmObject(type, modal.row.id, body);
      } else {
        await createCrmObject(type, body);
      }
      setModal(null);
      await load();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo guardar",
        text: err.response?.data?.message || err.message || "Revisa los campos e inténtalo de nuevo.",
        confirmButtonColor: brand.primary,
      });
    }
  };

  const handleDelete = async (row: CrmObject & CrudRow) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminar registro",
      text: "Se borra sólo de este CRM. El registro en HubSpot se conserva.",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#DC2626",
    });
    if (!res.isConfirmed) return;
    try {
      await deleteCrmObject(type, row.id);
      await load();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo eliminar",
        text: err.response?.data?.message || err.message || "",
        confirmButtonColor: brand.primary,
      });
    }
  };

  const handlePush = async (row: CrmObject & CrudRow) => {
    if (!def) return;
    setBusyId(row.id);
    try {
      const r = await pushToHubspot(def.collection, row.id);
      Swal.fire({
        icon: r.outcome ? "success" : "info",
        title: r.outcome ? "Enviado a HubSpot" : "Sin cambios",
        text: r.outcome
          ? `El registro se ${r.outcome === "created" ? "creó" : "actualizó"} en HubSpot.`
          : `No se envió: ${r.skipped}.`,
        confirmButtonColor: brand.primary,
      });
      await load();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo enviar",
        text: err.response?.data?.message || err.message || "",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setBusyId(null);
    }
  };

  // The HubSpot link column is appended to whatever the caller asked for.
  const allColumns: CrudColumn<CrmObject & CrudRow>[] = useMemo(
    () => [
      ...columns,
      {
        key: "hubspotId",
        label: "HubSpot",
        align: "center",
        render: (r) =>
          r.hubspotId ? (
            <Badge value="Sincronizado" />
          ) : (
            <span className="text-[11px] text-gray-400">—</span>
          ),
      },
    ],
    [columns],
  );

  const writable = def?.writable ?? false;

  const actions = (row: CrmObject & CrudRow) => (
    <div className="flex items-center justify-center gap-1">
      <button
        title="Enviar a HubSpot ahora"
        onClick={() => handlePush(row)}
        disabled={busyId === row.id}
        className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10 disabled:opacity-40"
      >
        <i className={busyId === row.id ? "ri-loader-4-line animate-spin" : "ri-upload-cloud-2-line"} />
      </button>
      {writable && (
        <>
          <button
            title="Editar"
            onClick={() => setModal({ mode: "edit", row })}
            className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
          >
            <i className="ri-edit-line" />
          </button>
          <button
            title="Eliminar"
            onClick={() => handleDelete(row)}
            className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
          >
            <i className="ri-delete-bin-line" />
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {icon && (
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
                <i className={`${icon} text-xl`} />
              </span>
            )}
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>{title}</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                {subtitle ? `${subtitle} · ` : ""}
                {total} registro{total === 1 ? "" : "s"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar…"
                className={`bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            <button
              onClick={load}
              title="Actualizar"
              className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.buttonHover}`}
            >
              <i className={loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} />
            </button>
            {writable && (
              <button
                onClick={() => setModal({ mode: "add" })}
                className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400"
              >
                <i className="ri-add-line" />
                <span className="whitespace-nowrap">Nuevo</span>
              </button>
            )}
          </div>
        </div>

        {error ? (
          <div className={`rounded-2xl border p-8 text-center ${tc.cardBorder} ${tc.cardBg}`}>
            <i className="ri-error-warning-line mb-2 text-2xl text-red-500" />
            <p className="text-sm text-red-500">{error}</p>
            <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-white hover:bg-primary-400">
              Reintentar
            </button>
          </div>
        ) : (
          <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <DataTable
              rows={rows}
              columns={allColumns}
              renderActions={actions}
              pageSize={12}
              emptyText={loading ? "Cargando…" : "Sin registros"}
            />
          </div>
        )}

        {!writable && (
          <p className={`mt-3 text-xs ${tc.textSecondary}`}>
            <i className="ri-lock-line mr-1" />
            HubSpot es el sistema de origen de estos registros: aquí son de sólo lectura y se actualizan en cada
            sincronización de Marketing.
          </p>
        )}
      </div>

      {modal && (
        <Modal
          title={modal.mode === "edit" ? `Editar — ${title}` : `Nuevo — ${title}`}
          onClose={() => setModal(null)}
          themeClasses={tc}
          content={
            <EntityForm
              fields={formFields}
              initial={modal.row}
              themeClasses={tc}
              submitLabel={modal.mode === "edit" ? "Guardar" : "Crear"}
              onCancel={() => setModal(null)}
              onSubmit={handleSubmit}
            />
          }
        />
      )}
    </div>
  );
}
